<!--
  Status Spedizioni — lista status spedizioni.
  Redesigned 2026-06-15 (v2.8.0, Phase 1.5 Chunk F.1).

  Drops kendo-grid + kendo-datepicker (era 2× nel container) +
  kendo-dropdownlist + mat-spinner + ~50 inline styles
  (41 kendo refs → 0).

  Pattern Diakonix table-card:
    - Sticky header con titolo + counter
    - Filter bar nativa (search + date range + select corriere + chip mese)
    - Table Diakonix con padding 14×16
    - Status pill colorato per status code spedizione
    - Per-row actions: 📡 Tracking (solo se tracking number presente)
      + ℹ️ Dettagli (sempre presente)
    - Click row → goToDetails (sub-modale dettagli F.2)

  Sub-modali preserved:
    - app-accesso-funzionari-tracking (modale tracking esterno)
    - app-status-spedizioni-dettaggli (modale dettagli, Chunk F.2)
-->

<div class="spedizioni-page">

  <!-- ─── Sticky header ────────────────────────────────────────── -->
  <header class="spedizioni-header">
    <div class="spedizioni-header__titles">
      <h1 class="spedizioni-header__title">🚚 Status spedizioni</h1>
      <p class="spedizioni-header__subtitle">
        @if (filteredOrders.length > 0) {
          <strong>{{ filteredOrders.length }}</strong>
          {{ filteredOrders.length === 1 ? 'spedizione' : 'spedizioni' }}
          @if (filterTerm) { · ricerca <strong>"{{ filterTerm }}"</strong> }
          @if (chosenCarrier) { · corriere <strong>{{ chosenCarrier }}</strong> }
          @if (selectedMonth) { · mese <strong>{{ selectedMonth }}</strong> }
        } @else if (loading) {
          Caricamento spedizioni…
        } @else {
          Nessuna spedizione registrata
        }
      </p>
    </div>

    <button
      type="button"
      class="refresh-btn"
      (click)="getShipmentList()"
      title="Aggiorna lista"
      aria-label="Aggiorna lista"
    >🔁</button>
  </header>

  <!-- ─── Filter bar ───────────────────────────────────────────── -->
  <section class="spedizioni-filters" aria-label="Filtri ricerca">

    <!-- Search -->
    <div class="filter-field">
      <label class="filter-field__label" for="searchSpedizioni">Cerca spedizione</label>
      <div class="filter-field__input-wrap">
        <input
          id="searchSpedizioni"
          type="search"
          class="filter-field__input"
          placeholder="N° ordine o cliente…"
          [(ngModel)]="filterTerm"
          (keyup.enter)="searchOrders()"
        />
        @if (filterTerm) {
          <button
            type="button"
            class="filter-field__clear"
            (click)="clearSearch()"
            aria-label="Rimuovi filtro"
          >×</button>
        }
      </div>
    </div>

    <!-- Date range -->
    <div class="filter-field filter-field--dates">
      <label class="filter-field__label">Data ritiro</label>
      <div class="filter-field__date-pair">
        <input
          type="date"
          class="filter-field__input filter-field__input--date"
          [ngModel]="filters.startingDate | date:'yyyy-MM-dd'"
          (ngModelChange)="onStartDateChange($event)"
          aria-label="Data da"
        />
        <span class="filter-field__date-sep">→</span>
        <input
          type="date"
          class="filter-field__input filter-field__input--date"
          [ngModel]="filters.endDate | date:'yyyy-MM-dd'"
          (ngModelChange)="onEndDateChange($event)"
          aria-label="Data a"
        />
      </div>
    </div>

    <!-- Carrier dropdown -->
    <div class="filter-field">
      <label class="filter-field__label" for="filterCarrier">Corriere</label>
      <select
        id="filterCarrier"
        class="filter-field__input filter-field__input--select"
        [(ngModel)]="chosenCarrier"
        (ngModelChange)="onCarrierChange($event)"
      >
        @for (carrier of carriers; track carrier) {
          <option [ngValue]="carrier === showAll ? null : carrier">
            {{ carrier || '— non specificato —' }}
          </option>
        }
      </select>
    </div>

    <button
      type="button"
      class="btn btn--primary"
      (click)="searchOrders()"
      [disabled]="loading"
    >🔍 Cerca</button>
  </section>

  <!-- ─── Month chip filter row ─────────────────────────────────── -->
  <section class="month-chips" aria-label="Filtra per mese">
    <button
      type="button"
      class="month-chip"
      [class.month-chip--active]="selectedMonth === null"
      (click)="onMonthSelect(null)"
    >Tutti</button>
    @for (m of monthNames; track m) {
      <button
        type="button"
        class="month-chip"
        [class.month-chip--active]="selectedMonth === m"
        (click)="onMonthSelect(m)"
      >{{ m }}</button>
    }
  </section>

  <!-- ─── Table card ───────────────────────────────────────────── -->
  <section class="spedizioni-table-card" aria-label="Elenco spedizioni">

    @if (loading) {
      <div class="table-skeleton" aria-busy="true">
        @for (i of [1, 2, 3, 4, 5, 6, 7, 8]; track i) {
          <div class="table-skeleton__row">
            <div class="table-skeleton__line table-skeleton__line--lg"></div>
            <div class="table-skeleton__line table-skeleton__line--md"></div>
            <div class="table-skeleton__line table-skeleton__line--sm"></div>
          </div>
        }
      </div>
    } @else if (filteredOrders.length === 0) {
      <div class="spedizioni-empty">
        <div class="spedizioni-empty__icon" aria-hidden="true">
          @if (filterTerm || chosenCarrier || selectedMonth) { 🔎 } @else { 📭 }
        </div>
        <p class="spedizioni-empty__msg">
          @if (filterTerm || chosenCarrier || selectedMonth) {
            Nessuna spedizione corrisponde ai filtri attivi.
          } @else {
            Nessuna spedizione registrata al momento.
          }
        </p>
      </div>
    } @else {
      <div class="spedizioni-table-scroll">
        <table class="spedizioni-table" role="table">
          <thead>
            <tr>
              <th class="col-order is-sortable" (click)="sortBy('or_order_number')">
                N° ordine
                @if (sortColumn === 'or_order_number') {
                  <span class="sort-indicator">{{ sortDirection === 'asc' ? '▲' : '▼' }}</span>
                }
              </th>
              <th class="col-customer is-sortable" (click)="sortBy('or_customer')">
                Cliente
                @if (sortColumn === 'or_customer') {
                  <span class="sort-indicator">{{ sortDirection === 'asc' ? '▲' : '▼' }}</span>
                }
              </th>
              <th class="col-carrier is-sortable" (click)="sortBy('sm_carrier_desc')">
                Corriere
                @if (sortColumn === 'sm_carrier_desc') {
                  <span class="sort-indicator">{{ sortDirection === 'asc' ? '▲' : '▼' }}</span>
                }
              </th>
              <th class="col-status is-sortable" (click)="sortBy('sm_status_desc')">
                Stato
                @if (sortColumn === 'sm_status_desc') {
                  <span class="sort-indicator">{{ sortDirection === 'asc' ? '▲' : '▼' }}</span>
                }
              </th>
              <th class="col-date is-sortable" (click)="sortBy('shipment_date')">
                Data ritiro
                @if (sortColumn === 'shipment_date') {
                  <span class="sort-indicator">{{ sortDirection === 'asc' ? '▲' : '▼' }}</span>
                }
              </th>
              <th class="col-date is-sortable" (click)="sortBy('or_closing_date')">
                Data consegna
                @if (sortColumn === 'or_closing_date') {
                  <span class="sort-indicator">{{ sortDirection === 'asc' ? '▲' : '▼' }}</span>
                }
              </th>
              <th class="col-actions">Azioni</th>
            </tr>
          </thead>
          <tbody>
            @for (
              item of filteredOrders;
              track item.order_id + '-' + ($index)
            ) {
              <tr
                class="spedizioni-row"
                [attr.data-status]="statusState(item)"
                (click)="goToDetails(item.order_id)"
                title="Apri dettagli spedizione"
              >
                <td class="col-order">
                  <span class="cell-order-num">{{ item.or_order_number || '—' }}</span>
                </td>
                <td class="col-customer">
                  <span class="cell-customer">{{ item.or_customer || '—' }}</span>
                </td>
                <td class="col-carrier">
                  <span class="cell-carrier">{{ item.sm_carrier_desc || '—' }}</span>
                </td>
                <td class="col-status">
                  <span
                    class="status-pill"
                    [attr.data-status]="statusState(item)"
                    [title]="item.sm_status_desc || ''"
                  >
                    @switch (statusState(item)) {
                      @case ('pending')    { ⏱ In attesa }
                      @case ('shipped')    { 📦 Spedito }
                      @case ('in-transit') { 🚚 In transito }
                      @case ('delivered')  { ✓ Consegnato }
                      @default             { — }
                    }
                  </span>
                </td>
                <td class="col-date">
                  <span class="cell-date">
                    {{ item.shipment_date ? (item.shipment_date | date:'dd/MM/yyyy') : '—' }}
                  </span>
                </td>
                <td class="col-date">
                  <span class="cell-date">
                    @if (!isClosingEmpty(item.or_closing_date)) {
                      {{ item.or_closing_date | date:'dd/MM/yyyy' }}
                    } @else { — }
                  </span>
                </td>
                <td class="col-actions" (click)="$event.stopPropagation()">
                  <div class="row-actions">
                    @if (item.sm_tracking_number) {
                      <button
                        type="button"
                        class="icon-btn icon-btn--tracking"
                        title="Apri tracking corriere"
                        (click)="goToTracking(item.order_id)"
                      >📡</button>
                    }
                    <button
                      type="button"
                      class="icon-btn icon-btn--primary"
                      title="Apri dettagli spedizione"
                      (click)="goToDetails(item.order_id)"
                    >ℹ️</button>
                  </div>
                </td>
              </tr>
            }
          </tbody>
        </table>
      </div>
    }
  </section>

</div>

<!-- ─── Sub-modali (preserved as-is) ──────────────────────────── -->

@if (closeServ.isAgentOrderTrack) {
  <div class="tracking-backdrop" (click)="closeTracking()">
    <div
      class="tracking-modal"
      (click)="$event.stopPropagation()"
      role="dialog"
      aria-modal="true"
      aria-labelledby="tracking-title"
    >
      <header class="tracking-modal__header">
        <h2 id="tracking-title" class="tracking-modal__title">
          <span aria-hidden="true">📡</span>
          Tracking spedizione
        </h2>
        <button
          type="button"
          class="tracking-modal__close"
          (click)="closeTracking()"
          aria-label="Chiudi"
        >×</button>
      </header>
      <div class="tracking-modal__body">
        <app-accesso-funzionari-tracking
          [shipment_id]="shipment_id"
          [openTrack]="closeServ.isAgentOrderTrack"
        ></app-accesso-funzionari-tracking>
      </div>
    </div>
  </div>
}

@if (closeServ.isID) {
  <app-status-spedizioni-dettaggli
    [parentId]="clickedId"
    [status_ID]="closeServ.isID"
  ></app-status-spedizioni-dettaggli>
}
